import { ALLOWED_LOGO_TYPES } from "@clinic/clinicBrandingClient";
import {
  useClinic,
  useUpdateBranding,
  useUploadClinicLogo,
} from "@clinic/clinicQueries";
import {
  Button,
  cn,
  SettingsCard,
  Textarea,
} from "@scribenote/brian-design-system";
import { useState } from "react";

/** No kit file input: the real one is hidden, so `peer-focus-visible` shows its focus here. */
const pickerFace = cn(
  "inline-flex items-center rounded-paper border border-rule bg-card px-3.5 py-1.5",
  "font-body text-sm font-medium text-ink",
  "transition duration-150 ease-paper motion-reduce:transition-none",
  "peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent",
);

/** Logo and mailing address applied to exported PDFs. */
export const ClinicBrandingPanel = () => {
  const { data: clinic } = useClinic();
  const updateBranding = useUpdateBranding();
  const uploadLogo = useUploadClinicLogo();

  const [address, setAddress] = useState<string | null>(null);

  const currentAddress = clinic?.address ?? "";
  const addressValue = address ?? currentAddress;

  const dirty = addressValue.trim() !== currentAddress.trim();
  const picking = !clinic || uploadLogo.isPending;

  const handleLogo = (files: FileList) => {
    const file = files[0];
    if (!file) return;
    // Silent: `accept` already filters the picker, so this is a deliberate override.
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) return;
    uploadLogo.mutate(file);
  };

  const handleSave = () => {
    updateBranding.mutate(
      { address: addressValue.trim() || null },
      { onSuccess: () => setAddress(null) },
    );
  };

  return (
    <SettingsCard
      title="Branding"
      subtitle="Your logo and address appear on exported PDFs."
      data-testid="clinic-branding-card"
    >
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-annotation-caps text-annotation">Logo</p>
          <div className="mt-1.5 flex items-center gap-3">
            {clinic?.logoUrl && (
              <img
                src={clinic.logoUrl}
                alt="Clinic logo"
                className="size-12 shrink-0 rounded-paper border border-rule bg-card object-contain"
              />
            )}
            <label
              className={cn(
                "flex items-center",
                picking ? "cursor-not-allowed" : "cursor-pointer",
              )}
            >
              <input
                type="file"
                accept={ALLOWED_LOGO_TYPES.join(",")}
                disabled={picking}
                onChange={(e) => {
                  if (e.target.files?.length) handleLogo(e.target.files);
                  e.target.value = "";
                }}
                className="peer sr-only"
                data-testid="clinic-logo-input"
              />
              <span
                className={cn(
                  pickerFace,
                  picking
                    ? "bg-recess text-faint"
                    : "hover:-translate-y-px hover:shadow-lift-lg",
                )}
              >
                {uploadLogo.isPending
                  ? "Uploading"
                  : clinic?.logoUrl
                    ? "Replace logo"
                    : "Upload logo"}
              </span>
            </label>
          </div>
        </div>

        <Textarea
          label="Address"
          value={addressValue}
          onChange={(e) => setAddress(e.target.value)}
          disabled={!clinic}
          minRows={2}
          placeholder="123 Main St, City, Province"
          data-testid="clinic-branding-address"
        />

        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!dirty}
            busy={updateBranding.isPending}
            onClick={handleSave}
            data-testid="clinic-branding-save"
          >
            {updateBranding.isPending ? "Saving" : "Save"}
          </Button>
        </div>
      </div>
    </SettingsCard>
  );
};
